import React, { useCallback } from 'react';
import type {
	Download,
	DownloadCallback,
	OnSave,
} from '@gov-nx/component/common';
import { GovError } from '@gov-nx/core/app';
import { useDownloadEvents } from '@gov-nx/core/events';
import { useQueue } from './useQueue';

type File = Download & DownloadCallback<string>;

interface DownloadListenerProps {
	onSave: OnSave;
	onAntivirus: (props: {
		onAccept: () => void;
		onDeny: () => void;
		fileName: string;
	}) => React.ReactNode;
}

export const DownloadListenerCore = (props: DownloadListenerProps) => {
	const fileQueue = useQueue<File>({
		onNewItem: async (file) => {
			if (!file.antivirus) {
				await download(file);
			}
		},
	});

	useDownloadEvents({
		onDownload: async (_, file) => fileQueue.add(file),
	});

	const download = useCallback(
		async (file: File) => {
			await props.onSave(file);
			if (file.callback) {
				file.callback(file.id, true, null);
			}
			fileQueue.remove(file);
		},
		[props, fileQueue]
	);

	const onAntivirusDeny = useCallback(() => {
		if (!fileQueue.first) return;

		if (fileQueue.first.callback) {
			fileQueue.first.callback(
				fileQueue.first.id,
				null,
				new GovError('[File] File download denied due to antivirus')
			);
		}
		fileQueue.remove(fileQueue.first);
	}, [fileQueue]);

	const onAntivirusAccept = useCallback(async () => {
		if (!fileQueue.first) return;

		await download(fileQueue.first);
	}, [download, fileQueue.first]);

	if (fileQueue.first?.antivirus) {
		return (
			<>
				{props.onAntivirus({
					fileName: fileQueue.first.fileName,
					onAccept: onAntivirusAccept,
					onDeny: onAntivirusDeny,
				})}
			</>
		);
	}

	return null;
};
